import { addDays, differenceInDays, isSameWeek, isThisWeek, isWeekend } from 'date-fns';
import { upperFirst } from 'lodash';

import { DeliveryTime } from 'api/api';

import { isOneOf } from '../logic';
import { availableLocaleConfigs } from '../translations/localization';
import { FormatDateFn, TranslateICUFn } from '../translations/translate';
import * as intervalFormatters from './delivery-time-interval-formatters';
import * as maxFormatters from './delivery-time-max-formatters';
import { formatTimespan } from './timespan';

const WEEK_LENGTH = 7;

export const getDeliveryTimeText: DeliveryTimeFunction = ({
  deliveryTime,
  translate,
  format,
  locale,
  additional = {},
}) => {
  if (deliveryTime.custom_text) {
    return deliveryTime.custom_text;
  }

  const estimateDetails = upperFirst(
    getDeliveryEstimateDetails(deliveryTime, translate, format, locale)
  );
  const timespanPart = formatTimespan(deliveryTime.timespan, translate, deliveryTime.use_timespan);

  const estimateDisclaimer = additional.showDeliveryTimeAsEstimate
    ? translate('widget.delivery_time.ESTIMATED')
    : '';

  if (timespanPart) {
    return `${estimateDetails} ${timespanPart} ${estimateDisclaimer}`.trimEnd();
  } else {
    return `${estimateDetails} ${estimateDisclaimer}`.trimEnd();
  }
};

const getDeliveryEstimateDetails = (
  deliveryTime: DeliveryTime,
  translate: TranslateICUFn,
  format: FormatDateFn,
  locale: string
) => {
  const { deliveryWeekMin, deliveryWeekMax } = getDeliveryWeeks(deliveryTime, locale);

  const deliveryWeeksAreTheSame = deliveryWeekMin === deliveryWeekMax;
  const deliveryDaysAreTheSame = deliveryTime.max === deliveryTime.min;
  let range: DeliveryTime['range'] = 'INTERVAL';
  if (
    deliveryTime.range === 'MAX' ||
    deliveryDaysAreTheSame ||
    (deliveryWeeksAreTheSame && isOneOf(['weeks', 'weeks_numbers'], deliveryTime.format)) ||
    // Avoid saying 'Delivered between 0 - x weeks
    (deliveryWeekMin === 0 &&
      deliveryWeekMax > 1 &&
      isOneOf(['weeks', 'weeks_numbers'], deliveryTime.format))
  ) {
    range = 'MAX';
  }

  switch (range) {
    case 'MAX':
      return getDeliveryTimeAsMax({
        deliveryTime,
        translate,
        format,
        locale,
        deliveryWeeksAreTheSame,
        deliveryDaysAreTheSame,
      });
    case 'INTERVAL':
      return getDeliveryTimeAsInterval({ deliveryTime, translate, format, locale });
  }
};

const getDeliveryTimeAsMax: MaxFormattingFunction = params => {
  const { deliveryTime, translate, locale } = params;
  const { max, unit = 'day' } = deliveryTime;

  if (
    (deliveryTime.format === 'weeks' || deliveryTime.format === 'weeks_numbers') &&
    unit === 'day'
  ) {
    const deliveryDate = addDaysToNow(max);
    const localeConfig = availableLocaleConfigs[locale];
    const deliveryDateIsNextWeek = isSameWeek(deliveryDate, addDaysToNow(WEEK_LENGTH), {
      locale: localeConfig,
    });

    if (isThisWeek(deliveryDate, { locale: localeConfig })) {
      return translate('widget.short_delivery_times.THIS_WEEK');
    }
    if (deliveryDateIsNextWeek) {
      return translate('widget.short_delivery_times.NEXT_WEEK');
    }
  }

  if (unit === 'business_day' || unit === 'day') {
    if (max === 0) {
      return translate('widget.short_delivery_times.TODAY');
    }
    if (max === 1 && unit === 'day') {
      return translate('widget.short_delivery_times.TOMORROW');
    }
    if (max === 1 && unit === 'business_day') {
      // handle the case where unit === 'business_day' and format is weekday
      // should say delivered on monday instead of 1 business day
      return isWeekend(addDays(new Date(), max))
        ? translate('widget.short_delivery_times.MAX_BUSINESS_DAYS', { max })
        : translate('widget.short_delivery_times.TOMORROW');
    }
  }

  switch (unit) {
    case 'business_day':
    case 'day':
      switch (deliveryTime.format || 'days') {
        case 'days':
          return maxFormatters.formatDays(params);
        case 'date':
          return maxFormatters.formatDate(params);
        case 'weekday':
          return maxFormatters.formatWeekday(params);
        case 'weekday_date':
          return maxFormatters.formatWeekdayDate(params);
        case 'weeks':
          return maxFormatters.formatWeeks(params);
        case 'weeks_numbers':
          return maxFormatters.formatWeekNumbers(params);
      }
    case 'hour':
      return translate('widget.short_delivery_times.MAX_HOUR', { max });
    case 'minute':
      return translate('widget.short_delivery_times.MAX_MINUTE', { max });
  }

  return '';
};

const getDeliveryTimeAsInterval: IntervalFormattingFunction = params => {
  const { deliveryTime, translate, locale } = params;
  const { min, max, unit = 'day' } = deliveryTime;

  const minDeliveryDate = addDaysToNow(min);
  const maxDeliveryDate = addDaysToNow(max);
  const maxDeliveryDateIsNextWeek = isSameWeek(maxDeliveryDate, addDaysToNow(7), {
    locale: availableLocaleConfigs[locale],
  });

  if (isOneOf(['weeks', 'weeks_numbers'], deliveryTime.format) && unit === 'day') {
    if (
      isThisWeek(minDeliveryDate, { locale: availableLocaleConfigs[locale] }) &&
      maxDeliveryDateIsNextWeek
    ) {
      return translate('widget.short_delivery_times.INTERVAL_THIS_NEXT_WEEK');
    }
  }

  if (unit === 'day' || unit === 'business_day') {
    if (min === 0 && max === 1) {
      return translate('widget.short_delivery_times.INTERVAL_TODAY_TOMORROW');
    }
  }

  switch (unit) {
    case 'business_day':
    case 'day':
      switch (deliveryTime.format || 'days') {
        case 'days':
          return intervalFormatters.formatDays(params);
        case 'date':
          return intervalFormatters.formatDate(params);
        case 'weekday':
          return intervalFormatters.formatWeekday(params);
        case 'weekday_date':
          return intervalFormatters.formatWeekdayDate(params);
        case 'weeks':
          return intervalFormatters.formatWeeks(params);
        case 'weeks_numbers':
          return intervalFormatters.formatWeekNumbers(params);
      }
    case 'hour':
      return translate('widget.short_delivery_times.INTERVAL_HOUR', { min, max });
    case 'minute':
      return translate('widget.short_delivery_times.INTERVAL_MINUTE', { min, max });
  }

  return '';
};

export const addDaysToNow = (days: number) => addDays(new Date(), days);

export const getDeliveryWeeks = (deliveryTime: DeliveryTime, locale: string) => {
  const minDeliveryDate = addDaysToNow(deliveryTime.min);
  const maxDeliveryDate = addDaysToNow(deliveryTime.max);
  const diffInDaysMin = differenceInDays(minDeliveryDate, new Date()) / 7;
  const diffInDaysMax = differenceInDays(maxDeliveryDate, new Date()) / 7;
  const localeConfig = availableLocaleConfigs[locale];

  const maxDeliveryDateIsThisWeek = isThisWeek(maxDeliveryDate, {
    locale: localeConfig,
  });
  const minDeliveryDateIsThisWeek = isThisWeek(minDeliveryDate, {
    locale: localeConfig,
  });

  const maxDeliveryDateIsNextWeek = isSameWeek(maxDeliveryDate, addDaysToNow(7), {
    locale: localeConfig,
  });
  const minDeliveryDateIsNextWeek = isSameWeek(minDeliveryDate, addDaysToNow(7), {
    locale: localeConfig,
  });

  return {
    deliveryWeekMin: minDeliveryDateIsThisWeek
      ? 0
      : minDeliveryDateIsNextWeek
        ? 1
        : Math.ceil(diffInDaysMin),
    deliveryWeekMax: maxDeliveryDateIsThisWeek
      ? 0
      : maxDeliveryDateIsNextWeek
        ? 1
        : Math.ceil(diffInDaysMax),
  };
};

type ShortDeliveryTimeAdditionalArguments = {
  showDeliveryTimeAsEstimate?: boolean;
};

export type MaxFormattingFunction = (params: {
  deliveryTime: DeliveryTime;
  translate: TranslateICUFn;
  format: FormatDateFn;
  deliveryWeeksAreTheSame: boolean;
  deliveryDaysAreTheSame: boolean;
  locale: string;
}) => string;

export type DeliveryTimeFunction = (params: {
  deliveryTime: DeliveryTime;
  translate: TranslateICUFn;
  format: FormatDateFn;
  locale: string;
  additional?: ShortDeliveryTimeAdditionalArguments;
}) => string;

export type IntervalFormattingFunction = (params: {
  deliveryTime: DeliveryTime;
  translate: TranslateICUFn;
  format: FormatDateFn;
  locale: string;
}) => string;
